import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../../core/auth/auth.service';
import { ThemeService } from '../../../core/services/theme.service';

/**
 * The only header on the application form (focus mode, AppComponent.focusMode). The product's
 * sidebar, balance, account toggle and notifications point at an account that doesn't exist yet,
 * so they're left out: the section list is the one navigation, the save bar the one action.
 */
@Component({
  selector: 'app-focus-header',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, MatIconModule, MatMenuModule, MatDividerModule, MatTooltipModule],
  template: `
    <header class="focus-bar">
      <div class="brand">
        <span class="logo">KLIDI <span class="dot" aria-hidden="true">●</span></span>
        <span class="divider" aria-hidden="true"></span>
        <span class="title">Account application</span>
      </div>

      <div class="actions">
        <a class="help" href="mailto:business@klidi.io">
          <mat-icon aria-hidden="true">help_outline</mat-icon><span class="help-text">Need help?</span>
        </a>

        <button type="button" class="icon-btn" (click)="theme.toggle()"
                [attr.aria-label]="theme.theme() === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'"
                [matTooltip]="theme.theme() === 'dark' ? 'Light mode' : 'Dark mode'">
          <mat-icon>{{ theme.theme() === 'dark' ? 'light_mode' : 'dark_mode' }}</mat-icon>
        </button>

        <!-- Sections save as they're completed; leaving goes to the overview of what's left. -->
        <a class="exit" routerLink="/application/individual-dashboard">
          <mat-icon aria-hidden="true">close</mat-icon><span class="exit-text">Exit</span>
        </a>

        <button type="button" class="user-trigger" [matMenuTriggerFor]="userMenu" aria-label="User menu">
          <span class="avatar">{{ auth.initials() || 'U' }}</span>
        </button>
        <mat-menu #userMenu="matMenu" class="user-menu" xPosition="before">
          <div class="user-menu-header" mat-menu-item disabled>
            <div class="user-menu-name">{{ auth.displayName() || 'User' }}</div>
          </div>
          <mat-divider></mat-divider>
          <button mat-menu-item routerLink="/application/account">
            <mat-icon>person_outline</mat-icon><span>Account</span>
          </button>
          <button mat-menu-item (click)="auth.logout()">
            <mat-icon>logout</mat-icon><span>Sign out</span>
          </button>
        </mat-menu>
      </div>
    </header>
  `,
  styles: [`
    :host { display:block; flex-shrink:0; }
    .focus-bar { display:flex; align-items:center; justify-content:space-between; gap:16px;
                 height:var(--header-h); padding:0 24px; box-sizing:border-box;
                 background:var(--header-bg); border-bottom:1px solid var(--border); }
    .brand { display:flex; align-items:center; gap:14px; min-width:0; }
    .logo { font:800 20px var(--font-sans); letter-spacing:1px; color:var(--text-primary); white-space:nowrap; }
    .dot { color:var(--green); font-size:13px; }
    .divider { width:1px; height:22px; background:var(--border); }
    .title { font-size:14px; font-weight:600; color:var(--text-secondary); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
    .actions { display:flex; align-items:center; gap:8px; }
    .help, .exit { display:inline-flex; align-items:center; gap:6px; height:36px; padding:0 12px;
                   border-radius:var(--radius-pill); font:600 13px var(--font-sans); text-decoration:none;
                   color:var(--text-secondary); white-space:nowrap; }
    .help:hover { color:var(--text-primary); background:var(--bg-hover); }
    .exit { border:1px solid var(--border); background:var(--bg-input); color:var(--text-primary); }
    .exit:hover { border-color:var(--border-strong); background:var(--bg-hover); }
    .help mat-icon, .exit mat-icon { font-size:18px; width:18px; height:18px; }
    .icon-btn { width:36px; height:36px; display:flex; align-items:center; justify-content:center;
                background:var(--bg-input); border:1px solid var(--border); border-radius:var(--radius-pill);
                color:var(--text-secondary); cursor:pointer; }
    .icon-btn:hover { background:var(--bg-hover); color:var(--text-primary); }
    .icon-btn mat-icon { font-size:19px; }
    .user-trigger { padding:2px; background:transparent; border:1px solid transparent; border-radius:50%; cursor:pointer; }
    .user-trigger:hover { border-color:var(--border); }
    .avatar { width:32px; height:32px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center;
              background:linear-gradient(135deg, var(--green) 0%, var(--green-dark) 100%);
              color:#fff; font-size:12px; font-weight:700; }
    .user-menu-name { font-weight:700; font-size:13px; color:var(--text-primary); }
    @media (max-width: 600px) {
      .focus-bar { padding:0 12px; }
      .divider, .title, .help-text, .exit-text { display:none; }
      .exit { padding:0 9px; }
    }
  `],
})
export class FocusHeaderComponent {
  protected readonly auth = inject(AuthService);
  protected readonly theme = inject(ThemeService);
}
